import { setBuffer } from "../interop/bufferManager";
import { getSvgFromByteArray } from "./imageFunctions";
import { getAttributedStringBufferInfo } from "./textEditor";

interface ClipboardFileInfo {
  id: number;
  name: string;
  mimeType: string;
}

function defaultFileName(file: File): string {
  return file.type.startsWith("image/") ? "image" : "file";
}

let dataTransfer: DataTransfer | null = null;

let clipboardNextFileBlobId = 0;
const clipboardFileBlobs = new Map<number, File>();

function saveBlobsAndCreateFileResult(files: File[]): ClipboardFileInfo[] {
  const filesResult: ClipboardFileInfo[] = [];
  for (const file of files) {
    const fileId = clipboardNextFileBlobId++;
    clipboardFileBlobs.set(fileId, file);
    filesResult.push({
      id: fileId,
      name: file.name || defaultFileName(file),
      mimeType: file.type,
    });
  }

  return filesResult;
}

export function clipboardEvent(event: ClipboardEvent) {
  dataTransfer = event.clipboardData;
  event.preventDefault();
}

export function clipboardDropEvent(event: DragEvent) {
  dataTransfer = event.dataTransfer;
  event.preventDefault();
}

export function isNavigatorClipboardApiSupported() {
  // @ts-expect-error 'clipboard.write' might still be unavailable when 'clipboard.read' is
  return !!(navigator?.clipboard?.write && navigator.clipboard.read);
}

export function isNavigatorClipboardWriteSupported() {
  return !!(navigator?.clipboard?.write);
}

export function clearData() {
  if (!dataTransfer) return;
  try {
    dataTransfer.clearData();
  } catch (error) {
    console.error(error);
  }
}

export function dataTransferTryReadPlainText() {
  if (!dataTransfer) return null;
  try {
    return dataTransfer.getData("text");
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function dataTransferTryReadAttributedString() {
  if (!dataTransfer) return null;
  try {
    const attrStringJson = dataTransfer.getData("axure/attributed-string");
    if (!attrStringJson) return null;
    const attrStringObj = JSON.parse(attrStringJson);
    return getAttributedStringBufferInfo(attrStringObj);
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function dataTransferTryReadFiles() {
  if (!dataTransfer) return null;
  try {
    const files = [];
    const items = dataTransfer.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i]!;
      if (item.kind === "file") {
        const file = item.getAsFile();
        if (file && file.size > 0) {
          files.push(file);
        }
      }
    }
    if (files.length > 0) {
      const filesResult = saveBlobsAndCreateFileResult(files);
      return JSON.stringify(filesResult);
    }
    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function readFileIntoBuffer(fileId: number): Promise<number> {
  return new Promise((resolve, reject) => {
    const file = clipboardFileBlobs.get(fileId);
    if (!file) {
      reject(new Error(`Invalid request to read file ${fileId} blob. File is not found in clipboard state`));
      return;
    }
    const reader = new FileReader();

    reader.onload = () => {
      if (reader.result instanceof ArrayBuffer) {
        const bufferId = setBuffer(reader.result);
        resolve(bufferId);
      } else {
        reject(new Error(`Failed to read file ${fileId} as buffer`));
      }
    };

    reader.onerror = () => {
      reject(reader.error ?? new Error(`Failed to read file ${fileId} as buffer`));
    };

    reader.readAsArrayBuffer(file);
  });
}

export function deleteFile(fileId: number) {
  clipboardFileBlobs.delete(fileId);
}

export function dataTransferTryReadAsHtmlBlob(): string | null {
  if (!dataTransfer) return null;
  try {
    return dataTransfer.getData("text/html");
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function writePlainText(text: string) {
  if (!dataTransfer) {
    await navigator.clipboard.writeText(text);
    return true;
  }
  try {
    dataTransfer.setData("text", text);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export function writeHtmlBlobToDataTransfer(html: string): boolean {
  if (!dataTransfer) return false;
  try {
    dataTransfer.setData("text/html", html);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export async function writeHtmlBlob(html: string): Promise<boolean> {
  try {
    if (!isNavigatorClipboardApiSupported()) return false;

    const clipboardItems = [new ClipboardItem({
      "text/html": new Blob([html], { type: "text/html" }),
    })];
    await navigator.clipboard.write(clipboardItems);
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
}

export async function getMimeTypes() {
  try {
    if (!isNavigatorClipboardApiSupported()) return "";

    const types: string[] = [];
    const cItems = await navigator.clipboard.read();
    if (cItems?.length) {
      for (const cItem of cItems) {
        types.push(...cItem.types);
      }
    }

    return types.join(";");
  } catch (error) {
    console.error(error);
    return "";
  }
}

export async function getTextData() {
  try {
    return await navigator.clipboard.readText();
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function tryReadAsHtmlBlobFromClipboardApi() {
  try {
    if (!isNavigatorClipboardApiSupported()) return null;
    const items = await navigator.clipboard.read();
    for (const item of items) {
      if (item.types.includes("text/html")) {
        const blob = await item.getType("text/html");
        const html = await blob.text();
        return html;
      }
    }
    return null;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export async function getImageData(mimeType: string) {
  if (!isNavigatorClipboardApiSupported()) {
    throw new Error(`Failed to get ${mimeType} image data. Clipboard API isn't supported`);
  }
  const items = await navigator.clipboard.read();
  if (!items || items.length === 0) {
    throw new Error(`Failed to get ${mimeType} image data. No items are present in Clipboard`);
  }
  for (const item of items) {
    const itemData = await item.getType(mimeType);
    if (itemData) {
      const blobData = await itemData.arrayBuffer();

      // rasterize SVG
      if (mimeType === "image/svg+xml") {
        const buffer = new Uint8Array(blobData);
        const bufferId = await getSvgFromByteArray(buffer);
        return bufferId;
      }

      return setBuffer(blobData);
    }
  }
  throw new Error(`Failed to get ${mimeType} image data. Mime type not found in Clipboard`);
}

export async function writeAxvg(axvg: string) {
  if (!isNavigatorClipboardApiSupported()) {
    throw new Error(`Failed write axvg string to clipboard. Clipboard API isn't supported`);
  }
  await navigator.clipboard.writeText(axvg);
}
